import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { ProviderSelector } from "@/components/providers/ProviderSelector";
import { AnimatedDialog, AlertMessage, Button } from "@/components/reusable";
import { MultiFileUploadField } from "@/components/upload/MultiFileUploadField";
import { schemaRequiresReportType } from "@/config/reportType";
import { canSubmitPendingPipelineUploads, type PendingPipelineUpload } from "@/lib/pipelineTypes";
import type { TargetSchema } from "@/types";
import { PendingPipelineFilesList } from "./PendingPipelineFilesList";

interface AddFilesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pendingEntries: PendingPipelineUpload[];
  onPendingFilesAdd: (files: File[]) => void;
  onPendingEntrySchemaChange: (index: number, schemaId: string) => void;
  onPendingAllSchemasChange: (schemaId: string) => void;
  onPendingEntryReportTypeChange: (index: number, reportType: string) => void;
  onPendingEntryRemove: (index: number) => void;
  onUploadError: (message: string) => void;
  schemas: TargetSchema[];
  isLoadingSchemas: boolean;
  onSubmit: () => void;
  isSubmitting: boolean;
  error?: string;
  showCancelBatch?: boolean;
  onCancelBatch?: () => void;
  maxFiles?: number;
  /** When true, provider must be selected before files/schemas. */
  providerRequired?: boolean;
  providerId?: string | null;
  onProviderChange?: (providerId: string | null) => void;
}

export function AddFilesDialog({
  open,
  onOpenChange,
  pendingEntries,
  onPendingFilesAdd,
  onPendingEntrySchemaChange,
  onPendingAllSchemasChange,
  onPendingEntryReportTypeChange,
  onPendingEntryRemove,
  onUploadError,
  schemas,
  isLoadingSchemas,
  onSubmit,
  isSubmitting,
  error,
  showCancelBatch = false,
  onCancelBatch,
  maxFiles,
  providerRequired = false,
  providerId = null,
  onProviderChange,
}: AddFilesDialogProps) {
  const schemaNameById = Object.fromEntries(schemas.map((schema) => [schema.id, schema.name]));
  const providerReady = !providerRequired || providerId !== null;
  const canSubmit =
    providerReady &&
    canSubmitPendingPipelineUploads(pendingEntries, schemaNameById, schemaRequiresReportType) &&
    !isSubmitting &&
    !isLoadingSchemas;

  const handleFilesChange = (files: File[]) => {
    if (!providerReady) {
      onUploadError("Select a provider first.");
      return;
    }
    onPendingFilesAdd(files);
  };

  return (
    <AnimatedDialog
      open={open}
      onOpenChange={onOpenChange}
      maxWidth="max-w-4xl"
      className="h-[min(85vh,44rem)]"
    >
      <div className="relative flex h-full flex-col p-6">
        <Dialog.Close asChild>
          <button
            type="button"
            className="absolute right-4 top-4 rounded-md p-1 text-[var(--color-text-tertiary)] hover:bg-[var(--color-bg-secondary)] hover:text-[var(--color-text-primary)]"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </Dialog.Close>

        <Dialog.Title className="pr-8 text-lg font-bold text-[var(--color-text-primary)]">
          Add files
        </Dialog.Title>
        <Dialog.Description className="mt-1 text-sm text-[var(--color-text-secondary)]">
          {providerRequired
            ? "Select a provider, then upload files and assign a target schema to each. Lloyd's files also require a report type."
            : "Upload files and assign a target schema to each file. Lloyd's files also require a report type."}
        </Dialog.Description>

        <div className="mt-5 flex min-h-0 flex-1 flex-col gap-4">
          {providerRequired ? (
            <div data-testid="add-files-provider">
              <label className="mb-2 block text-sm font-semibold text-[var(--color-text-primary)]">
                Provider
              </label>
              <ProviderSelector
                value={providerId}
                onChange={(next) => onProviderChange?.(next)}
                allowedStatus="active"
                emptyMessage="None of your assigned providers are active yet. Contact an admin."
              />
            </div>
          ) : null}

          <MultiFileUploadField
            files={pendingEntries.map((entry) => entry.file)}
            onFilesChange={handleFilesChange}
            onError={onUploadError}
            maxFiles={maxFiles}
            showFileList={false}
            disabled={!providerReady}
            className="max-w-none w-full shrink-0"
          />
          {providerRequired && !providerId ? (
            <p
              className="text-xs text-[var(--color-text-tertiary)]"
              data-testid="add-files-provider-hint"
            >
              Select a provider to see schemas.
            </p>
          ) : null}

          {providerReady ? (
            <div className="min-h-0 flex-1 overflow-hidden">
              <PendingPipelineFilesList
                entries={pendingEntries}
                schemas={schemas}
                isLoadingSchemas={isLoadingSchemas}
                onEntrySchemaChange={onPendingEntrySchemaChange}
                onAllSchemasChange={onPendingAllSchemasChange}
                onEntryReportTypeChange={onPendingEntryReportTypeChange}
                onRemoveEntry={onPendingEntryRemove}
              />
            </div>
          ) : null}

          {error ? <AlertMessage variant="error" message={error} /> : null}

          <div className="flex shrink-0 flex-wrap items-center gap-3 pt-1">
            <Button
              type="button"
              onClick={onSubmit}
              disabled={!canSubmit}
              data-testid="add-files-submit-btn"
            >
              {isSubmitting ? "Uploading…" : "Upload & extract"}
            </Button>
            <Dialog.Close asChild>
              <Button type="button" variant="secondary" disabled={isSubmitting}>
                Cancel
              </Button>
            </Dialog.Close>
            {showCancelBatch && onCancelBatch ? (
              <Button
                type="button"
                variant="secondary"
                onClick={onCancelBatch}
                disabled={isSubmitting}
                data-testid="cancel-batch-btn"
              >
                Cancel active batch
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    </AnimatedDialog>
  );
}
